import { ChangeDetectionStrategy, Component, forwardRef, inject, OnInit, Provider } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { FormsUtil, WizardStep } from 'mbs-ui-kit';
import { minimalWindowsVersionForNewAsync } from 'mbs-ui-shared@models/Computer';
import { first } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ClusterStepFormValue, ClusterStepValue } from 'wizards/models/cluster-models';
import { HostType } from 'wizards/models/select-host-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForTestCredentials, WizardStepsService } from 'wizards/services/wizard-steps.service';

import { AlertComponent, FormGroupComponent, InputComponent, InputPasswordComponent, RadioComponent, RadioDescription } from 'mbs-ui-kit';

import { I18NextPipe } from 'angular-i18next';

import { ReactiveFormsModule } from '@angular/forms';
import { StepBase } from 'wizards/steps/StepBase.class';

const ClusterStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => ClusterStepComponent),
  multi: true
};

@Component({
  selector: 'mbs-cluster-step',
  templateUrl: './cluster-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClusterStepValueAccessor],
  imports: [
    AlertComponent,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    InputPasswordComponent,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule
  ]
})
export class ClusterStepComponent extends StepBase<ClusterStepValue> implements OnInit {
  mainService: RemoteManagementWizardsService;
  private stepService = inject(WizardStepsService);

  private isValidCredentials;
  private needCheckVMCredentials = true;
  private notUpdatePassToInitial = false;

  public initialValue: ClusterStepValue;

  private valueAfterVerified: ClusterStepValue;

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  hostCredentialsIsValid(): ValidationErrors | null {
    return this.isValidCredentials === false ? { invalidCredentials: true } : null;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new FormGroup<ClusterStepFormValue>({
      clusterBackup: new FormControl(false),
      login: new FormControl({ value: '', disabled: true }, [Validators.required, this.hostCredentialsIsValid.bind(this)]),
      password: new FormControl({ value: '', disabled: true }, [Validators.required, this.hostCredentialsIsValid.bind(this)]),
      remember: new FormControl({ value: false, disabled: true }),
      skipCredentials: new FormControl({ value: false, disabled: true })
    });

    this.initFormEvents();
  }

  onStepFormChange(value: ClusterStepValue): void {
    if (this.valueAfterVerified) {
      this.needCheckVMCredentials =
        value.clusterBackup && (this.valueAfterVerified.login !== value.login || this.valueAfterVerified.password !== value.password);
    } else this.needCheckVMCredentials = !!value.clusterBackup;

    this.value = { ...value, valid: this.stepForm.valid && (this.isRDMode || this.isOffline || !this.needCheckVMCredentials) };
  }

  forceValid(step: WizardStep & { isSave?: boolean; isNext?: boolean } = null): void {
    if (this.isRDMode || this.isOffline || this.value?.valid) return;

    if (this.needCheckVMCredentials && step.isNext) return void this.runCheckVMCredentials(step.isSave);

    FormsUtil.triggerValidation(this.stepForm);
  }

  updateForm(value: ClusterStepValue): void {
    if (!value) return;

    if (this.isEdit) {
      this.initialValue = value;
      this.valueAfterVerified = value;
    }

    this.stepForm.reset(value);

    this.clusterBackupChangeHandler(value.clusterBackup);
  }

  clusterBackupChangeHandler(state: boolean): void {
    this.toggleFormControls(['login', 'password', 'remember', 'skipCredentials'], state);
  }

  resetIsValidCredentials(): void {
    if (this.isValidCredentials === false) {
      this.isValidCredentials = null;
      this.needCheckVMCredentials = true;
      this.stepForm.get('login').updateValueAndValidity();
      this.stepForm.get('password').updateValueAndValidity();
    }
  }

  passwordFocusHandler(event): void {
    if (event?.target?.value && this.initialValue && event.target.value === this.initialValue.password) {
      this.stepForm.get('password').setValue('');
    }
  }

  passwordBlurHandler(event): void {
    if (event?.target && !event.target.value && this.initialValue?.password && !this.notUpdatePassToInitial) {
      this.stepForm.get('password').setValue(this.initialValue.password);
    }
  }

  // Private section
  private runCheckVMCredentials(isSave: boolean): void {
    this.loadInfo.emit({ loading: true });

    const needOldAsync = (+this.mainService?.backupVersionUpdated?.substring(0, 3) || 0) < minimalWindowsVersionForNewAsync;

    this.stepService
      .getRemoteCommandData(this.getParams(), this.mainService.hid, needOldAsync)
      .pipe(
        first(),
        tap((result) => {
          this.isValidCredentials = !!result?.data?.result;

          if (this.isValidCredentials) {
            this.needCheckVMCredentials = false;
            this.valueAfterVerified = this.stepForm.value;
            this.notUpdatePassToInitial = this.valueAfterVerified?.password !== this.initialValue?.password;
          }

          FormsUtil.triggerValidation(this.stepForm);
          this.stepForm.updateValueAndValidity();

          if (this.stepForm.valid) this.nextStep.emit(isSave);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => this.loadInfo.emit({ loading: false }),
        error: (e) => {
          this.isValidCredentials = !e?.error?.detail?.includes('ESXi credential');

          FormsUtil.triggerValidation(this.stepForm);

          this.loadInfo.emit({ loading: false });
        }
      });
  }

  private getParams(): ParamsForTestCredentials {
    const formValue = this.stepForm.value;

    return {
      agentType: 'backup',
      commandType: 'CheckVMCredentials',
      params: {
        Type: HostType.HyperV,
        Server: '',
        Login: formValue.login,
        Password: formValue.password
      }
    };
  }
}
